// Translation loader - manages dynamic loading of translations based on route
import { commonTranslations } from './common';
import { homeTranslations } from './home';

type Language = 'en' | 'pt';
type TranslationRecord = Record<string, string>;
type Translations = { en: TranslationRecord; pt: TranslationRecord };

// Merge two translation objects
const mergeTranslations = (base: Translations, additional: Translations): Translations => ({
  en: { ...base.en, ...additional.en },
  pt: { ...base.pt, ...additional.pt },
});

// Base translations (common + home) loaded synchronously for homepage
export const baseTranslations: Translations = mergeTranslations(commonTranslations, homeTranslations);

// Route to translation module mapping
const routeTranslationMap: Record<string, () => Promise<{ default: Translations } | Translations>> = {
  // Portuguese routes
  '/registo-yango-motorista-maputo': () => import('./registo').then(m => ({ default: m.registoTranslations })),
  '/cadastro-motorista-yango': () => import('./cadastro').then(m => ({ default: m.cadastroTranslations })),
  '/requisitos-motorista-yango': () => import('./requisitos').then(m => ({ default: m.requisitosTranslations })),
  '/baixar-yango-pro-maputo': () => import('./download').then(m => ({ default: m.downloadTranslations })),
  '/blog': () => import('./blog').then(m => ({ default: m.blogTranslations })),
  '/recarga-automatica-yango-pro-emola-mpesa': () => import('./recarga').then(m => ({ default: m.recargaTranslations })),
  '/sobre-nos': () => import('./about').then(m => ({ default: m.aboutTranslations })),
  // English routes
  '/en/yango-driver-registration-maputo': () => import('./registo').then(m => ({ default: m.registoTranslations })),
  '/en/yango-driver-signup': () => import('./cadastro').then(m => ({ default: m.cadastroTranslations })),
  '/en/yango-driver-requirements': () => import('./requisitos').then(m => ({ default: m.requisitosTranslations })),
  '/en/download-yango-pro-maputo': () => import('./download').then(m => ({ default: m.downloadTranslations })),
  '/en/blog': () => import('./blog').then(m => ({ default: m.blogTranslations })),
  '/en/automatic-top-up-yango-pro': () => import('./recarga').then(m => ({ default: m.recargaTranslations })),
  '/en/about-us': () => import('./about').then(m => ({ default: m.aboutTranslations })),
};

// Load translations for a specific route
export const loadTranslationsForRoute = async (pathname: string): Promise<Translations | null> => {
  // Check if route matches any translation module
  for (const [route, loader] of Object.entries(routeTranslationMap)) {
    const isBlogRoute = route === '/blog' || route === '/en/blog';
    const matchesBlog = isBlogRoute && (pathname.startsWith('/blog') || pathname.startsWith('/en/blog'));
    
    if (pathname === route || pathname.startsWith(route + '/') || matchesBlog) {
      try {
        const module = await loader();
        return 'default' in module ? module.default : module;
      } catch (error) {
        console.warn(`Failed to load translations for route: ${route}`, error);
        return null;
      }
    }
  }
  return null;
};

// Get route key from pathname
export const getRouteKey = (pathname: string): string | null => {
  for (const route of Object.keys(routeTranslationMap)) {
    const isBlogRoute = route === '/blog' || route === '/en/blog';
    const matchesBlog = isBlogRoute && (pathname.startsWith('/blog') || pathname.startsWith('/en/blog'));
    
    if (pathname === route || pathname.startsWith(route + '/') || matchesBlog) {
      return route;
    }
  }
  return null;
};
